import { useCashflow } from '@/components/cashflow/CashflowContext';
import TransactionEntryItem from '@/components/cashflow/transactions/TransactionEntryItem';
import Callout from '@/components/common/Callout';
import { FlexRow } from '@/components/common/Flex';
import { Skeletons } from '@/components/common/Skeletons';
import { useTracking } from '@/services/analytics/useTracking';
import { Divider, FlexProps, VStack } from '@chakra-ui/react';
import { useHasNoFEs } from '@common/react/financialEntities';
import { dt } from '@common/util/dateTime';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { FlexCol } from '../common/Flex';
import HeaderLink from '../common/HeaderLink';

const DashboardTransactionsWidget = (props: FlexProps) => {
    const { recentTransactions, loading } = useRecentTransactions();
    const { track } = useTracking();
    const router = useRouter();
    const hasNoFEs = useHasNoFEs();

    return (
        <FlexCol
            bg="gray.50"
            rounded={8}
            overflow="auto"
            minH={72}
            maxH={540}
            {...props}
        >
            <FlexRow
                bg="gray.100"
                px={8}
                pt={6}
                pb={4}
                position="sticky"
                top={0}
                // If the user has no FEs, this is just placeholder content
                // Reduce zIndex so ConnectAccountOverlay is above this
                zIndex={hasNoFEs ? 0 : 1}
            >
                <HeaderLink
                    onClick={() => {
                        track('Dashboard Recent Transactions Header Clicked');
                        router.push('/transactions');
                    }}
                    title="Recent transactions"
                />
            </FlexRow>
            <FlexCol px={8} pb={4}>
                {loading ? (
                    <Skeletons count={4} w="100%" h={8} mt={4} />
                ) : recentTransactions.length === 0 ? (
                    <Callout px={0}>No transactions yet</Callout>
                ) : (
                    <VStack align="stretch" divider={<Divider />} spacing={0}>
                        {recentTransactions.map((item) => (
                            <TransactionEntryItem
                                key={item.id}
                                transactionEntry={item}
                            />
                        ))}
                    </VStack>
                )}
            </FlexCol>
        </FlexCol>
    );
};

const useRecentTransactions = () => {
    const { historicalTransactionEntries, loading } = useCashflow();

    return useMemo(() => {
        const thirtyDaysAgo = dt().subtract(30, 'days').toDateStr();
        const recentTransactions = historicalTransactionEntries.filter(
            (t) => t.date >= thirtyDaysAgo
        );

        return { recentTransactions, loading };
    }, [historicalTransactionEntries, loading]);
};
export default DashboardTransactionsWidget;
